import React, { useEffect, useContext, useState } from 'react';
import io from 'socket.io-client';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../../../../context/authContext';
import ChatNotificationSystem from './ChatNotificationSystem';

/**
 * Componente responsável por inicializar e gerenciar a conexão WebSocket global do admin
 * Este componente deve ser montado no App.tsx para garantir conexão permanente
 */
interface WebSocketManagerProps {
  children?: React.ReactNode;
  enableDebug?: boolean;
}

interface ConnectionStatus {
  isConnected: boolean;
  connectionId?: string;
  error?: string;
  backendUrl?: string;
  hotelId?: number;
  lastConnectedAt?: Date;
}

// Global socket instance
let globalSocket: ReturnType<typeof io> | null = null;

export const WebSocketManager: React.FC<WebSocketManagerProps> = ({ 
  children, 
  enableDebug = true 
}) => {
  const location = useLocation();
  const { loginInfo } = useContext(AuthContext);
  const [status, setStatus] = useState<ConnectionStatus>({
    isConnected: false
  });

  const hotelId = loginInfo?.pluginspaceId || 1;
  const authToken = loginInfo?.accessToken || 'no-token';
  const shouldConnectGlobalSocket = location.pathname.includes('/communication-center');

  const log = (...args: any[]) => {
    if (enableDebug) {
      console.log('[WebSocketManager]', ...args);
    }
  };

  const initializeWebSocket = async () => {
    try {
      // Fechar conexão anterior se existir
      if (globalSocket?.connected) {
        log('🔄 Fechando conexão anterior...');
        globalSocket.disconnect();
      }

      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:7004/api';
      const wsUrl = backendUrl.replace('/api', '');

      log('🔌 Iniciando conexão WebSocket...');
      log('📡 Backend URL:', backendUrl);
      log('🌐 WebSocket URL:', wsUrl);
      log('🏨 Hotel ID:', hotelId);
      log('🎫 Token presente:', !!authToken);

      // Testar conectividade do backend primeiro
      try {
        const testResponse = await fetch(`${wsUrl}/health`, {
          method: 'GET'
        });

        if (!testResponse.ok) {
          throw new Error(`Backend não disponível: ${testResponse.status}`);
        }
        log('✅ Backend está online');
      } catch (testError) {
        log('⚠️ Teste de conectividade falhou, tentando conexão direta:', testError);
      }

      // Criar nova conexão
      globalSocket = io(wsUrl, {
        path: '/socket.io',
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 10000,
        transports: ['websocket'], // 🔥 WebSocket-only (compatível com WebApp)
        upgrade: false, // 🔥 Não tentar upgrade (manter WebSocket puro)
        query: {
          token: authToken,
          userType: 'agent',
          hotelId: hotelId.toString(),
          source: 'admin',
          timestamp: Date.now().toString()
        },
      });

      // Event listeners
      globalSocket.on('connect', () => {
        log('✅ WebSocket conectado com sucesso!');
        log('🆔 Socket ID:', globalSocket?.id);
        log('🚀 Transport: websocket/polling');

        setStatus({
          isConnected: true,
          connectionId: globalSocket?.id,
          backendUrl: wsUrl,
          hotelId,
          lastConnectedAt: new Date()
        });

        // Registrar como admin ativo
        globalSocket?.emit('admin-connected', { 
          hotelId,
          userType: 'agent',
          source: 'admin',
          timestamp: Date.now()
        });

        // Entrar na sala do hotel
        globalSocket?.emit('join-hotel', { 
          hotelId, 
          source: 'admin',
          userType: 'agent' 
        });

        log('🏨 Admin registrado para hotel:', hotelId);
      });

      globalSocket.on('connect_error', (error: any) => {
        log('❌ Erro de conexão:', error.message);
        setStatus(prev => ({
          ...prev,
          isConnected: false,
          error: error.message
        }));
      });

      globalSocket.on('disconnect', (reason: any) => {
        log('❌ WebSocket desconectado:', reason);
        setStatus(prev => ({
          ...prev,
          isConnected: false
        }));
      });

      globalSocket.on('reconnect', (attempt: any) => {
        log('🔄 Reconectado após tentativa:', attempt);
        setStatus(prev => ({
          ...prev,
          isConnected: true,
          error: undefined
        }));
      });

      // Listeners para mensagens do chat
      const messageEvents = [
        'guest-new-message',
        'force-refresh-messages', 
        'multi-session-message',
        'bidirectional-message',
        'webapp-message',
        'guest-message',
        'new-message',
        'message',
        'session-message',
        'hotel-message'
      ];

      messageEvents.forEach(event => {
        globalSocket?.on(event, (data: any) => {
          log(`📨 [${event}] Mensagem recebida:`, data);
          
          // Dispatch custom event para componentes que precisam escutar
          window.dispatchEvent(new CustomEvent('websocket-message', {
            detail: { event, data, timestamp: Date.now() }
          }));
        });
      });

      log('🎯 Todos os listeners configurados');

    } catch (error: any) {
      log('❌ Erro na inicialização:', error);
      setStatus(prev => ({
        ...prev,
        isConnected: false,
        error: error.message
      }));
    }
  };

  // Inicializar WebSocket global apenas em rotas que precisam de real-time do communication center
  useEffect(() => {
    if (!shouldConnectGlobalSocket) {
      if (globalSocket?.connected) {
        log('⏸️ Fora da rota do communication center, desconectando WebSocket global...');
        globalSocket.disconnect();
      }
      globalSocket = null;
      setStatus({ isConnected: false });
      return;
    }

    if (loginInfo && hotelId) {
      log('👤 Usuário logado detectado, inicializando WebSocket...');
      initializeWebSocket();
    }

    // Cleanup ao desmontar
    return () => {
      if (globalSocket?.connected) {
        log('🧹 Limpando conexão WebSocket...');
        globalSocket.disconnect();
        globalSocket = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginInfo, hotelId, shouldConnectGlobalSocket]);

  // Expor socket globalmente para outros componentes
  useEffect(() => {
    (window as any).globalSocket = globalSocket;
    (window as any).webSocketStatus = status;
  }, [status]);

  return (
    <>
      {/* Sistema de notificações de chat */}
      <ChatNotificationSystem />
      
      {/* {enableDebug && (
        <div 
          style={{
            position: 'fixed',
            bottom: 10,
            right: 10,
            background: status.isConnected ? '#28a745' : '#dc3545',
            color: 'white',
            padding: '5px 10px',
            borderRadius: '5px',
            fontSize: '12px',
            zIndex: 9999,
            fontFamily: 'monospace'
          }}
          title={JSON.stringify(status, null, 2)}
        >
          🔌 WebSocket: {status.isConnected ? '✅ Online' : '❌ Offline'}
          {status.connectionId && <div>ID: {status.connectionId.slice(-8)}</div>}
        </div>
      )} */}
      {children}
    </>
  );
};

// Hook para acessar o socket global de outros componentes
export const useGlobalSocket = () => {
  const [isConnected, setIsConnected] = useState(globalSocket?.connected || false);

  useEffect(() => {
    const updateStatus = () => {
      setIsConnected(globalSocket?.connected || false);
    };

    const interval = setInterval(updateStatus, 1000);
    return () => clearInterval(interval);
  }, []);

  return {
    socket: globalSocket,
    isConnected,
    emit: (event: string, data: any) => {
      if (globalSocket?.connected) {
        globalSocket.emit(event, data);
        return true;
      }
      return false;
    }
  };
};

export default WebSocketManager;